// src/DimensionnementRulesManager.js
import React, { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';

const fmt = (n) => new Intl.NumberFormat('fr-FR').format(Math.round(n||0));

const RULES_DEFAULT = {
  seuil_min_kit_auto: 1000,
  seuil_panneau_625: 27,
  seuil_triphase: 30000,
  panneau_545: { ref: 'JKM545M-72HL4-BDVP', nom: 'Panneau Jinko Tiger Neo 545Wc', puissance: 545, prix: 152220 },
  panneau_625: { ref: 'JKM625M-72HL4-BDVP', nom: 'Panneau Jinko Tiger Neo Bifacial 625Wc', puissance: 625, prix: 174560 },
  batterie_gel: { ref: 'GEL-200AH-12V', nom: 'Batterie GEL SunXellence 200Ah 12V', prix: 265640 },
  batterie_lithium_small: { ref: 'SRNE-5.12KWH', nom: 'Batterie SRNE SR-SE05B 5.12kWh', prix: 1103100 },
  batterie_lithium_large: { ref: 'SRNE-EOS15B', nom: 'Batterie SRNE SR-EOS15B 16.07kWh', prix: 2909300 },
  forfait_installation: [
    { max_wc: 1500, prix: 87350 }, { max_wc: 3000, prix: 124750 },
    { max_wc: 5000, prix: 173120 }, { max_wc: 8000, prix: 198640 },
    { max_wc: 20000, prix: 350000 }, { max_wc: 999999, prix: 500000 },
  ],
  paliers_panneaux: [
    { max_panneaux: 4,  onduleur: 'SRNE HF2430S80-H', nb_onduleurs: 1, tension: 24, nb_gel: 2, nb_lithium: 0 },
    { max_panneaux: 6,  onduleur: 'SRNE HF2430S80-H', nb_onduleurs: 1, tension: 24, nb_gel: 4, nb_lithium: 0 },
    { max_panneaux: 10, onduleur: 'SRNE-HYP-5KW',     nb_onduleurs: 1, tension: 48, nb_gel: 4, nb_lithium: 1 },
    { max_panneaux: 18, onduleur: 'SRNE-HYP-5KW',     nb_onduleurs: 2, tension: 48, nb_gel: 4, nb_lithium: 2 },
    { max_panneaux: 27, onduleur: 'SRNE-HYP-5KW',     nb_onduleurs: 3, tension: 48, nb_gel: 4, nb_lithium: 3 },
    { max_panneaux: 37, onduleur: 'SRNE-HYP-5KW',     nb_onduleurs: 4, tension: 48, nb_gel: 4, nb_lithium: 2 },
    { max_panneaux: 43, onduleur: 'SRNE-HYP-5KW',     nb_onduleurs: 5, tension: 48, nb_gel: 4, nb_lithium: 3 },
    { max_panneaux: 49, onduleur: 'SRNE-HYP-5KW',     nb_onduleurs: 6, tension: 48, nb_gel: 4, nb_lithium: 3 },
  ],
};

const inputStyle = {
  border: '1px solid #e5e7eb', borderRadius: '6px', padding: '5px 8px',
  fontSize: '13px', outline: 'none', width: '100%', background: 'white',
};
const labelStyle = { fontSize: '12px', fontWeight: 600, color: '#6b7280', marginBottom: '4px', display: 'block' };

const DimensionnementRulesManager = () => {
  const [rules, setRules] = useState(RULES_DEFAULT);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.from('dimensionnement_rules').select('cle, valeur')
      .then(({ data }) => {
        if (data && data.length > 0) {
          const r = { ...RULES_DEFAULT };
          data.forEach(row => {
            try { r[row.cle] = typeof row.valeur === 'string' ? JSON.parse(row.valeur) : row.valeur; }
            catch { r[row.cle] = row.valeur; }
          });
          setRules(r);
        }
        setLoading(false);
      });
  }, []);

  const updateRule = (cle, valeur) => setRules(prev => ({ ...prev, [cle]: valeur }));

  const updatePalier = (idx, field, val) => {
    const updated = rules.paliers_panneaux.map((p, i) => i === idx ? { ...p, [field]: val } : p);
    updateRule('paliers_panneaux', updated);
  };

  const updateForfait = (idx, field, val) => {
    const updated = rules.forfait_installation.map((f, i) => i === idx ? { ...f, [field]: val } : f);
    updateRule('forfait_installation', updated);
  };

  const sauvegarder = async () => {
    setSaving(true);
    const clesToSave = [
      'seuil_min_kit_auto', 'seuil_panneau_625', 'seuil_triphase',
      'panneau_545', 'panneau_625',
      'batterie_gel', 'batterie_lithium_small', 'batterie_lithium_large',
      'forfait_installation', 'paliers_panneaux',
    ];
    for (const cle of clesToSave) {
      await supabase.from('dimensionnement_rules').upsert(
        { cle, valeur: rules[cle] },
        { onConflict: 'cle' }
      );
    }
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  if (loading) return <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>Chargement des règles...</div>;

  const sectionStyle = {
    background: 'white', borderRadius: '12px', padding: '20px',
    border: '1px solid #e5e7eb', marginBottom: '16px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
  };
  const titleStyle = { fontWeight: 700, fontSize: '15px', color: '#1f2937', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' };

  return (
    <div style={{ padding: '24px', maxWidth: '1100px', margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <h2 style={{ fontSize: '20px', fontWeight: 800, color: '#1f2937', margin: 0 }}>⚙️ Règles de dimensionnement PV</h2>
          <p style={{ fontSize: '13px', color: '#6b7280', margin: '4px 0 0' }}>Paramètres utilisés par le module de dimensionnement automatique</p>
        </div>
        <button onClick={sauvegarder} disabled={saving}
          style={{ background: saved ? '#10b981' : '#1e40af', color: 'white', border: 'none', borderRadius: '8px', padding: '10px 24px', fontSize: '14px', fontWeight: 700, cursor: 'pointer', transition: 'all 0.2s' }}>
          {saving ? '⏳ Sauvegarde...' : saved ? '✅ Sauvegardé !' : '💾 Sauvegarder'}
        </button>
      </div>

      {/* ── Seuils ── */}
      <div style={sectionStyle}>
        <div style={titleStyle}>🎯 Seuils de configuration</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
          <div>
            <label style={labelStyle}>Consommation min. kit auto (Wh/j)</label>
            <input type="number" value={rules.seuil_min_kit_auto} onChange={e => updateRule('seuil_min_kit_auto', parseInt(e.target.value)||0)} style={inputStyle} />
            <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>En dessous : kit manuel recommandé</div>
          </div>
          <div>
            <label style={labelStyle}>Nb panneaux bascule vers 625Wc Bifacial</label>
            <input type="number" value={rules.seuil_panneau_625} onChange={e => updateRule('seuil_panneau_625', parseInt(e.target.value)||0)} style={inputStyle} />
            <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>Au-dessus : Jinko Tiger Neo 625Wc</div>
          </div>
          <div>
            <label style={labelStyle}>Puissance bascule triphasé (Wc)</label>
            <input type="number" value={rules.seuil_triphase} onChange={e => updateRule('seuil_triphase', parseInt(e.target.value)||0)} style={inputStyle} />
            <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>Au-dessus : options triphasées proposées</div>
          </div>
        </div>
      </div>

      {/* ── Panneaux ── */}
      <div style={sectionStyle}>
        <div style={titleStyle}>🔆 Panneaux solaires</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
          {['panneau_545', 'panneau_625'].map(cle => (
            <div key={cle} style={{ background: '#f8fafc', borderRadius: '8px', padding: '14px', border: '1px solid #e5e7eb' }}>
              <div style={{ fontSize: '13px', fontWeight: 700, color: '#1f2937', marginBottom: '10px' }}>
                {cle === 'panneau_545' ? '🌟 Panneau standard' : '⭐ Panneau grands kits (bifacial)'}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div><label style={labelStyle}>Référence</label><input value={rules[cle]?.ref||''} onChange={e => updateRule(cle, { ...rules[cle], ref: e.target.value })} style={inputStyle} /></div>
                <div><label style={labelStyle}>Nom</label><input value={rules[cle]?.nom||''} onChange={e => updateRule(cle, { ...rules[cle], nom: e.target.value })} style={inputStyle} /></div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  <div><label style={labelStyle}>Puissance (Wc)</label><input type="number" value={rules[cle]?.puissance||0} onChange={e => updateRule(cle, { ...rules[cle], puissance: parseInt(e.target.value)||0 })} style={inputStyle} /></div>
                  <div><label style={labelStyle}>Prix (FCFA)</label><input type="number" value={rules[cle]?.prix||0} onChange={e => updateRule(cle, { ...rules[cle], prix: parseInt(e.target.value)||0 })} style={inputStyle} />
                    <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>{fmt(rules[cle]?.prix)} FCFA</div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Batteries ── */}
      <div style={sectionStyle}>
        <div style={titleStyle}>🔋 Batteries</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
          {[
            { cle: 'batterie_gel', label: '🔋 GEL 200Ah 12V' },
            { cle: 'batterie_lithium_small', label: '⚡ Lithium 5.12kWh (SR-SE05B)' },
            { cle: 'batterie_lithium_large', label: '⚡ Lithium 16.07kWh (SR-EOS15B)' },
          ].map(({ cle, label }) => (
            <div key={cle} style={{ background: '#f8fafc', borderRadius: '8px', padding: '14px', border: '1px solid #e5e7eb' }}>
              <div style={{ fontSize: '13px', fontWeight: 700, color: '#1f2937', marginBottom: '10px' }}>{label}</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div><label style={labelStyle}>Référence</label><input value={rules[cle]?.ref||''} onChange={e => updateRule(cle, { ...rules[cle], ref: e.target.value })} style={inputStyle} /></div>
                <div><label style={labelStyle}>Nom</label><input value={rules[cle]?.nom||''} onChange={e => updateRule(cle, { ...rules[cle], nom: e.target.value })} style={inputStyle} /></div>
                <div><label style={labelStyle}>Prix (FCFA)</label>
                  <input type="number" value={rules[cle]?.prix||0} onChange={e => updateRule(cle, { ...rules[cle], prix: parseInt(e.target.value)||0 })} style={inputStyle} />
                  <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>{fmt(rules[cle]?.prix)} FCFA</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Paliers panneaux ── */}
      <div style={sectionStyle}>
        <div style={titleStyle}>📊 Paliers dimensionnement — Panneaux / Onduleurs / Batteries</div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ background: '#f1f5f9' }}>
                {['Max panneaux', 'Onduleur (ref)', 'Nb onduleurs', 'Tension V', 'Nb GEL', 'Nb Lithium small'].map(h => (
                  <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 700, color: '#374151', borderBottom: '2px solid #e5e7eb', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(rules.paliers_panneaux || []).map((p, idx) => (
                <tr key={idx} style={{ borderBottom: '1px solid #f3f4f6', background: idx % 2 === 0 ? 'white' : '#fafafa' }}>
                  <td style={{ padding: '8px 12px' }}>
                    <input type="number" value={p.max_panneaux} onChange={e => updatePalier(idx, 'max_panneaux', parseInt(e.target.value)||0)}
                      style={{ ...inputStyle, width: '80px', fontWeight: 700, color: '#1e40af' }} />
                  </td>
                  <td style={{ padding: '8px 12px' }}>
                    <select value={p.onduleur} onChange={e => updatePalier(idx, 'onduleur', e.target.value)}
                      style={{ ...inputStyle, width: '180px' }}>
                      <option value="SRNE HF2430S80-H">SRNE HF2430S80-H (3.3kW 24V)</option>
                      <option value="SRNE-HYP-5KW">SRNE-HYP-5KW (5kW 48V)</option>
                      <option value="SRNE-HESP-16KW">SRNE-HESP-16KW (16kW)</option>
                      <option value="SRNE-HESP-18KW">SRNE-HESP-18KW (18kW)</option>
                      <option value="SRNE-HESP-20KW">SRNE-HESP-20KW (20kW)</option>
                    </select>
                  </td>
                  <td style={{ padding: '8px 12px' }}>
                    <input type="number" value={p.nb_onduleurs} min="1" max="9" onChange={e => updatePalier(idx, 'nb_onduleurs', parseInt(e.target.value)||1)}
                      style={{ ...inputStyle, width: '70px', textAlign: 'center' }} />
                  </td>
                  <td style={{ padding: '8px 12px' }}>
                    <select value={p.tension} onChange={e => updatePalier(idx, 'tension', parseInt(e.target.value))}
                      style={{ ...inputStyle, width: '80px' }}>
                      <option value={24}>24V</option>
                      <option value={48}>48V</option>
                    </select>
                  </td>
                  <td style={{ padding: '8px 12px' }}>
                    <input type="number" value={p.nb_gel} min="0" max="20" onChange={e => updatePalier(idx, 'nb_gel', parseInt(e.target.value)||0)}
                      style={{ ...inputStyle, width: '70px', textAlign: 'center' }} />
                  </td>
                  <td style={{ padding: '8px 12px' }}>
                    <input type="number" value={p.nb_lithium} min="0" max="16" onChange={e => updatePalier(idx, 'nb_lithium', parseInt(e.target.value)||0)}
                      style={{ ...inputStyle, width: '70px', textAlign: 'center' }} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '8px' }}>
          💡 Les paliers sont appliqués dans l'ordre croissant. Le premier palier où max_panneaux ≥ nb_panneaux calculé est utilisé.
        </div>
      </div>

      {/* ── Forfaits installation ── */}
      <div style={sectionStyle}>
        <div style={titleStyle}>🔧 Forfaits installation par tranche de puissance</div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ background: '#f1f5f9' }}>
                {['Jusqu\'à (Wc)', 'Forfait installation (FCFA)'].map(h => (
                  <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 700, color: '#374151', borderBottom: '2px solid #e5e7eb' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(rules.forfait_installation || []).map((f, idx) => (
                <tr key={idx} style={{ borderBottom: '1px solid #f3f4f6', background: idx % 2 === 0 ? 'white' : '#fafafa' }}>
                  <td style={{ padding: '8px 12px' }}>
                    {f.max_wc === 999999 ? (
                      <span style={{ fontSize: '13px', fontWeight: 600, color: '#6b7280' }}>Au-dessus (industriel)</span>
                    ) : (
                      <input type="number" value={f.max_wc} onChange={e => updateForfait(idx, 'max_wc', parseInt(e.target.value)||0)}
                        style={{ ...inputStyle, width: '120px' }} />
                    )}
                  </td>
                  <td style={{ padding: '8px 12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input type="number" value={f.prix} onChange={e => updateForfait(idx, 'prix', parseInt(e.target.value)||0)}
                        style={{ ...inputStyle, width: '140px' }} />
                      <span style={{ fontSize: '12px', color: '#9ca3af' }}>{fmt(f.prix)} FCFA</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Bouton bas */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '8px' }}>
        <button onClick={sauvegarder} disabled={saving}
          style={{ background: saved ? '#10b981' : '#1e40af', color: 'white', border: 'none', borderRadius: '8px', padding: '12px 32px', fontSize: '15px', fontWeight: 700, cursor: 'pointer', transition: 'all 0.2s' }}>
          {saving ? '⏳ Sauvegarde...' : saved ? '✅ Sauvegardé !' : '💾 Sauvegarder les règles'}
        </button>
      </div>
    </div>
  );
};

export default DimensionnementRulesManager;
